import { TagOutlined } from '@ant-design/icons';
import WidgetModel, { TagsWidgetConfig } from '@mm/state/model/WidgetModel';
import { useViewActions } from '@mm/state/actions/viewActions';
import { StoreState } from '@mm/state/store-interfaces';
import React, { ReactNode } from 'react';
import classNames from 'classnames';
import { Handle, Position, NodeProps } from 'react-flow-renderer';
import { useSelector } from 'react-redux';
import common from '../index.module.css';
import css from './Tag.module.css';
import { useOpenOnReady } from '../use-open-on-ready';
import { uniqueId } from 'lodash';
import { Chip } from '@mm/components';
import pluralize from 'pluralize';

export const TagIcon = TagOutlined;

type Props = { className?: string } & NodeProps<{
  widget: WidgetModel;
}>;

export const Tag: React.FC<Props> = ({
  className,
  data,
  isDragging,
  children,
}) => {
  const widgets = useSelector((state: StoreState) => state.widget.entities);
  const widget = widgets.find(w => w.id === data.widget.id) || data.widget;
  const config = widget.config as Partial<TagsWidgetConfig>;
  const viewActions = useViewActions();
  const { kind, tags } = config;

  const onClick = () => {
    if (isDragging) {
      return;
    }
    viewActions.showSlideoutMenu('tagWidgetEditor', {
      widgetId: data.widget.id,
    });
  };

  useOpenOnReady(widget, onClick);

  return (
    <>
      <Handle type="target" position={Position.Top} />
      <div
        className={classNames(className, common.card, css.container)}
        onClick={onClick}
      >
        <div className={css.body}>
          <div className={css.header}>
            <div className={css.icon}>
              <TagIcon />
            </div>

            <div>
              {React.Children.map<ReactNode, ReactNode>(children, child => {
                if (React.isValidElement(child)) {
                  return React.cloneElement(child, {
                    // @ts-ignore onEdit does not seemto be present or types are off in @types/react
                    onEdit: onClick,
                    key: uniqueId(),
                  });
                }
              })}
            </div>

            <span className={css.placeholder}>Tags</span>
          </div>
          {!!tags?.length && (
            <div className={css.info}>
              <span className={css.kind}>
                {kind === 'add_tags' && 'Add:'}
                {kind === 'remove_tags' && 'Remove:'}
              </span>

              <span className={css.tags}>
                {tags.slice(0, 2).map(tag => (
                  <Chip
                    className={classNames(css.tag, {
                      [css.addTag]: kind === 'add_tags',
                      [css.removeTag]: kind === 'remove_tags',
                    })}
                    text={`${tag.name.slice(0, 10)}${
                      tag.name.length > 10 ? '...' : ''
                    }`}
                    key={tag.name}
                  />
                ))}
                {tags.length > 2 && (
                  <span className={css.tagsCounter}>
                    +{tags.length - 2} {pluralize('tag', tags.length - 2)}
                  </span>
                )}
              </span>
            </div>
          )}
        </div>
      </div>

      <Handle type="source" position={Position.Bottom} />
    </>
  );
};
